import { useNavigation } from "@luce/core/adapters/navigation";
import { Box, Card, cn, Divider, Icon, Image, Tag, Typography, type TagProps } from "@luce/ui";

import { ClientAccountTypeEnum, SubscriptionStatusEnum } from "../../__generated__/graphql";
import {
  AddPaymentMethodModal,
  useAddPaymentMethodEntry,
  useClientPaymentMethod,
} from "../../billing";
import { useClientProfile } from "../../shared/gqlhooks/use-client-profile";
import LucePlusImg from "../assets/luce-plus-logo-3d.png";
import { PLUS_DASHBOARD_PATH, PLUS_PAYMENT_METHOD_PATH } from "../constant";
import { useClientSubscription } from "../gqlhooks/use-client-subscription";

const NOTICE_TONES = {
  neutral: {
    container: "bg-luce-neutral-background-intermediate border-luce-neutral-border-highEmphasis",
    icon: "Info",
    iconClassName: "text-luce-neutral-foreground-intermediate",
    textColor: "neutral-foreground-intermediate",
  },
  warning: {
    container: "bg-luce-system-warning-surface border-luce-system-warning-border",
    icon: "WarningCircle",
    iconClassName: "text-luce-system-warning-onSurface",
    textColor: "system-warning-on-surface",
  },
  danger: {
    container: "bg-luce-system-danger-surface border-luce-system-danger-border",
    icon: "WarningCircle",
    iconClassName: "text-luce-system-danger-default",
    textColor: "system-danger-default",
  },
} as const;

type StatusView = {
  label: string;
  tagVariant: TagProps["variant"];
  tone: keyof typeof NOTICE_TONES;
  message: string;
  actionLabel: string;
};

const STATUS_VIEWS: Partial<Record<SubscriptionStatusEnum, StatusView>> = {
  [SubscriptionStatusEnum.Active]: {
    label: "Active",
    tagVariant: "info",
    tone: "neutral",
    message: "Manage or cancel your Luce Plus subscription from the Luce Plus page.",
    actionLabel: "Go to Luce Plus ↗",
  },
  [SubscriptionStatusEnum.Cancelled]: {
    label: "Cancelled",
    tagVariant: "warning",
    tone: "warning",
    message:
      "Your Luce Plus subscription is cancelled, but will remain active until the end of your current billing period. To view details or reactivate, go to the Luce Plus page.",
    actionLabel: "Go to Luce Plus ↗",
  },
  [SubscriptionStatusEnum.PendingPayment]: {
    label: "Payment Required",
    tagVariant: "danger",
    tone: "danger",
    message:
      "We couldn't charge your card for your Luce Plus subscription. Your subscription may be affected if the payment isn't completed. Please update your payment method to continue your subscription.",
    actionLabel: "Update Payment Method →",
  },
};

type LucePlusStatusCardProps = {
  clientId: string;
  onCardUpdated: () => void;
};

export const LucePlusStatusCard = ({ clientId, onCardUpdated }: LucePlusStatusCardProps) => {
  const { push } = useNavigation();
  const { client } = useClientProfile(clientId);
  const isIndividual = client?.accountType === ClientAccountTypeEnum.Individual;

  const { subscription } = useClientSubscription({
    clientId,
    skip: !isIndividual,
    fetchPolicy: "cache-and-network",
  });
  const paymentPending = subscription?.status === SubscriptionStatusEnum.PendingPayment;

  useClientPaymentMethod({ clientId, skip: !paymentPending, fetchPolicy: "cache-and-network" });
  const { usesModal, isModalOpen, onSetupCard, onSetupClose, onSetupSuccess } =
    useAddPaymentMethodEntry({ screenPath: PLUS_PAYMENT_METHOD_PATH, onSuccess: onCardUpdated });

  const view = subscription ? STATUS_VIEWS[subscription.status] : undefined;
  if (!view) return null;

  const tone = NOTICE_TONES[view.tone];
  const onAction = paymentPending ? onSetupCard : () => push(PLUS_DASHBOARD_PATH);

  return (
    <>
      <Card className="w-full">
        <Box className="gap-spacing-sm flex flex-row items-start">
          <Image src={LucePlusImg} alt="Luce Plus" width={24} height={24} />
          <Card.Header>
            <Card.Title>Auto Pay for Luce Plus</Card.Title>
            <Card.Description>
              Your Luce Plus subscription is automatically paid using this card.
            </Card.Description>
          </Card.Header>
          <Box className="gap-spacing-sm flex flex-col items-end">
            <Tag variant={view.tagVariant} size="sm">
              {view.label}
            </Tag>
          </Box>
        </Box>

        <Divider />

        <Box
          className={cn("flex flex-row items-start gap-2 rounded-2xl border p-3", tone.container)}
        >
          <Icon name={tone.icon} weight="fill" size={20} className={tone.iconClassName} />
          <Typography variant="body-sm" color={tone.textColor} className="flex-1">
            {view.message}{" "}
            <Typography
              variant="body-sm"
              color="brand-primary"
              className="cursor-pointer"
              onClick={onAction}
            >
              {view.actionLabel}
            </Typography>
          </Typography>
        </Box>
      </Card>

      {usesModal && paymentPending && (
        <AddPaymentMethodModal
          open={isModalOpen}
          onClose={onSetupClose}
          onSuccess={onSetupSuccess}
        />
      )}
    </>
  );
};
